// Anotar: hora, texto livre, Enviar. Nada mais.
//
// Pedido do dono, na letra: "a tal horário notei isso, isso e isso, clica em
// enviar". Quem escreve aqui está cansado, no quarto, às vezes de madrugada.
// Formulário de dez campos não seria preenchido.
//
// O texto é o registro principal. Os números que o sistema reconhece aparecem
// para um toque de confirmação — se ninguém confirmar, o texto salva do mesmo
// jeito. Estruturar é bônus; registrar é obrigação.
import { useMemo, useState, type FormEvent } from 'react';
import {
  UNIDADE_POR_TIPO, doCampoParaISO, formatarHora, agoraParaCampo,
} from '@medicov1/shared';
import { chamar } from '../api';
import { Ditar } from '../componentes/Ditar';
import { extrairDoTexto, ROTULO_ACHADO } from '../dados/extrairDoTexto';
import './anotar.css';

/**
 * Agora, no relógio de Brasília — não no do celular. Se o aparelho estiver em
 * outro fuso, o campo abria com a hora dele, e a gravação (que lê o campo como
 * Brasília) registrava a hora errada.
 */
const agoraLocal = () => agoraParaCampo();

interface Props {
  pacienteId: string;
  aoGravar: () => void;
  aoVoltar: () => void;
}

export function Anotar({ pacienteId, aoGravar, aoVoltar }: Props) {
  const [quando, setQuando] = useState(agoraLocal());
  const [texto, setTexto] = useState('');
  const [recusados, setRecusados] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  // Confirmação ocupa a tela: limpar o formulário e mostrar um aviso pequeno
  // deixava dúvida se tinha enviado ou se o texto se perdeu.
  const [pronto, setPronto] = useState<{ medidas: number; texto: string; quando: string } | null>(null);

  const achados = useMemo(() => extrairDoTexto(texto), [texto]);
  const aceitos = achados.filter((a) => !recusados.has(a.tipo));

  const alternar = (tipo: string) =>
    setRecusados((atual) => {
      const novo = new Set(atual);
      if (novo.has(tipo)) novo.delete(tipo); else novo.add(tipo);
      return novo;
    });

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!texto.trim()) { setErro('Escreva alguma coisa.'); return; }

    setErro('');
    setEnviando(true);
    const ocorreuEm = doCampoParaISO(quando);

    try {
      // A anotação vai primeiro: se o envio das medidas falhar, o que a pessoa
      // escreveu já está salvo.
      await chamar('/anotacoes', { metodo: 'POST', corpo: { pacienteId, texto, ocorreuEm } });

      // Sistólica e diastólica saíram da mesma frase: são um evento só, e o
      // grupo faz a linha do tempo mostrá-las juntas, como vindas do aparelho.
      const grupoPa = `anotacao-${Date.now()}`;
      const ehPa = (t: string) => t === 'pa_sistolica' || t === 'pa_diastolica';

      let gravadas = 0;
      for (const achado of aceitos) {
        try {
          await chamar('/leituras', {
            metodo: 'POST',
            corpo: {
              pacienteId, tipo: achado.tipo, valor: achado.valor,
              origem: 'manual', medidoEm: ocorreuEm,
              ...(ehPa(achado.tipo) ? { grupo: grupoPa } : {}),
            },
          });
          gravadas++;
        } catch {
          // Medida recusada não derruba a anotação — ela é o que importa.
        }
      }

      setPronto({ medidas: gravadas, texto, quando: ocorreuEm });
      setTexto('');
      setRecusados(new Set());
      setQuando(agoraLocal());
      aoGravar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
    } finally {
      setEnviando(false);
    }
  }

  if (pronto) {
    const hora = formatarHora(pronto.quando);
    return (
      <main className="anotar">
        <header className="anotar__topo">
          <h1>Anotar</h1>
        </header>

        <section className="salvo">
          <div className="salvo__marca" aria-hidden="true">✓</div>
          <h2>Anotação salva</h2>
          <p className="salvo__resumo">
            Registrada às <b>{hora}</b>
            {pronto.medidas > 0 && (
              <> com <b>{pronto.medidas} {pronto.medidas === 1 ? 'medida' : 'medidas'}</b></>
            )}. Já está na linha do tempo.
          </p>
          <blockquote className="salvo__texto">{pronto.texto}</blockquote>

          <div className="salvo__acoes">
            <button className="botao" onClick={() => setPronto(null)}>Fazer outra anotação</button>
            <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="anotar">
      <header className="anotar__topo">
        <h1>Anotar</h1>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      <form onSubmit={enviar} className="anotar__form">
        <div className="campo anotar__quando">
          <label htmlFor="quando">Quando foi</label>
          <input id="quando" type="datetime-local" value={quando}
            onChange={(e) => setQuando(e.target.value)} required />
        </div>

        <div className="campo">
          <label htmlFor="texto">O que você viu</label>
          {/* Ditar é mais rápido do que digitar no corredor, e o que a pessoa
              fala vira exatamente o texto que o sistema guarda. */}
          <div className="comDitado">
            <textarea
              id="texto"
              rows={6}
              autoFocus
              placeholder="A enfermeira passou, mediu sat 96 e temp 37,4. Ela dormiu bem a noite toda."
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
            <Ditar valor={texto} aoMudar={setTexto} rotulo="ditar a anotação" />
          </div>
        </div>

        {achados.length > 0 && (
          <div className="anotar__achados">
            <p className="anotar__achadosTitulo">
              Reconheci estes números. Toque para tirar o que não for medida:
            </p>
            <div className="anotar__chips">
              {achados.map((a) => {
                const fora = recusados.has(a.tipo);
                return (
                  <button
                    type="button"
                    key={a.tipo}
                    className={`chip${fora ? ' chip--fora' : ''}`}
                    onClick={() => alternar(a.tipo)}
                  >
                    <b>{ROTULO_ACHADO[a.tipo]}</b>
                    <span>{a.valor.toLocaleString('pt-BR')} {UNIDADE_POR_TIPO[a.tipo]}</span>
                    <small>“{a.trecho}”</small>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {erro && <p className="aviso-erro">{erro}</p>}

        <div className="anotar__acoes">
          <button className="botao" type="submit" disabled={enviando || !texto.trim()}>
            {enviando ? 'Salvando…' : 'Enviar'}
          </button>
          <span className="anotar__dica">
            {aceitos.length > 0
              ? `${aceitos.length} ${aceitos.length === 1 ? 'medida entra' : 'medidas entram'} junto com o texto.`
              : 'O texto é o que importa — os números são bônus.'}
          </span>
        </div>
      </form>
    </main>
  );
}
